import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { TallyKeyboardProvider } from './context/TallyKeyboardContext';
import ButtonBar from './components/finance/ButtonBar';
import Login from './pages/Login';
import KioskAttendance from './pages/KioskAttendance';
import Layout from './components/layout/Layout';
import CheckInWall, { useCheckInGate } from './components/CheckInWall';
import { LEGACY_PATH_REDIRECTS } from './config/modulePathAliases';
import { UNIVERSAL_MODULES } from './config/universalModules';
import { T as Tds } from './design-system/tokens';
import { APP_NAME } from './config/brand';

function BootSplash() {
  const [tookTooLong, setTookTooLong] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setTookTooLong(true), 10000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F4F2F7', fontFamily: 'Plus Jakarta Sans,Inter,sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: Tds.type.lg, fontWeight: 800, background: 'linear-gradient(135deg,#B1A2D8,#B1A2D8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '0.08em', marginBottom: 8 }}>
          CRAFT
        </div>
        <div style={{ fontSize: Tds.type.xs, color: '#9298AC' }}>Loading…</div>
        {tookTooLong && (
          <div style={{ marginTop: 18, fontSize: Tds.type.xs, color: '#6B7280' }}>
            <div style={{ marginBottom: 8 }}>This is taking longer than usual.</div>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '6px 14px', fontSize: Tds.type.xs, background: '#fff', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', marginRight: 6 }}
            >Retry</button>
            <button
              onClick={() => { window.location.href = '/login'; }}
              style={{ padding: '6px 14px', fontSize: Tds.type.xs, background: '#111', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >Go to login</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Stale-deploy chunk errors, across bundlers: webpack says "Loading chunk",
// Vite says "Failed to fetch dynamically imported module" / "error loading
// dynamically imported module"; Safari says "Importing a module script failed".
export function isStaleChunkError(error) {
  if (error?.name === 'ChunkLoadError') return true;
  const msg = error?.message || '';
  if (/Loading chunk|dynamically imported module|Importing a module script failed/i.test(msg)) return true;
  // A stale app shell whose lazy import resolved to a purged or renamed chunk
  // does not always fail the fetch — the module can load as something other
  // than a component, and React.lazy then dies on `.default`. Seen live on
  // /arey after a deploy, 2026-09-12: bundle index.C4mi-CxX.js still open,
  // index.K0fn3wsm.js live. Only treat it as stale when the loaded shell is
  // known to differ from the server's, so a real `.default` bug still surfaces.
  if (/reading 'default'|reading "default"|undefined \(reading 'default'\)/i.test(msg) && shellIsStale()) return true;
  return false;
}

// The script tag that loaded this shell vs the one index.html serves now.
// Async check cached on first call; synchronous readers see the last result.
let _shellStale = null;
export function shellIsStale() {
  if (_shellStale !== null) return _shellStale;
  _shellStale = false;
  try {
    const mine = [...document.scripts].map((s) => s.src).find((src) => /\/static\/js\/index\.[\w-]+\.js/.test(src));
    if (!mine) return false;
    fetch('/index.html', { cache: 'no-store' }).then((r) => r.text()).then((html) => {
      const m = html.match(/static\/js\/index\.[\w-]+\.js/);
      if (m && !mine.endsWith(m[0])) _shellStale = true;
    }).catch(() => {});
  } catch { /* non-browser */ }
  return _shellStale;
}
export function __setShellStaleForTests(v) { _shellStale = v; }

// Escalating recovery for a stale deploy. Attempt 1: plain reload (fresh
// index.html brings fresh chunk names). Attempt 2: the SW's cache-first static
// cache may still be serving old assets — unregister every SW, wipe all
// caches, then reload. After that, give up and let the error surface.
// Returns false when attempts are exhausted.
export function recoverFromStaleChunk() {
  let attempt = 0;
  try {
    const raw = JSON.parse(sessionStorage.getItem('chunk_recover') || 'null');
    // A fresh failure more than 2 min after the last one is a new incident
    if (raw && Date.now() - raw.at < 120000) attempt = raw.n;
    sessionStorage.setItem('chunk_recover', JSON.stringify({ n: attempt + 1, at: Date.now() }));
  } catch { /* storage blocked — still attempt one reload */ }
  if (attempt === 0) {
    window.location.reload();
    return true;
  }
  if (attempt === 1) {
    const reload = () => window.location.reload();
    const jobs = [];
    try {
      if ('serviceWorker' in navigator) {
        jobs.push(navigator.serviceWorker.getRegistrations().then(regs => Promise.all(regs.map(r => r.unregister()))));
      }
      if ('caches' in window) {
        jobs.push(caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k)))));
      }
      Promise.all(jobs).then(reload).catch(reload);
      setTimeout(reload, 4000); // belt-and-braces if a promise hangs
    } catch { reload(); }
    return true;
  }
  return false;
}

// Retry wrapper for lazy imports — recovers from stale-deploy chunk errors
function lazyRetry(importFn) {
  return React.lazy(() =>
    importFn().catch((error) => {
      if (recoverFromStaleChunk()) {
        return new Promise(() => {}); // never resolves, page will reload
      }
      throw error; // recovery exhausted — let the ErrorBoundary show it
    })
  );
}

// Error boundary to catch white-screen crashes and show useful error
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, recovering: false }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    // Stale deploy — old chunk purged from the server. Recover instead of
    // stranding the user on an error screen.
    if (isStaleChunkError(error) && recoverFromStaleChunk()) {
      this.setState({ recovering: true });
      return;
    }
    // The shell-staleness probe is async on first call; give it a moment and
    // look again before leaving the user on an error screen.
    if (/reading 'default'/i.test(error?.message || '')) {
      setTimeout(() => {
        if (shellIsStale() && recoverFromStaleChunk()) this.setState({ recovering: true });
      }, 1500);
    }
  }
  render() {
    if (this.state.recovering) {
      return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F7F3', fontFamily: 'Geist, Inter, sans-serif' }}>
          <div style={{ textAlign: 'center', color: '#6E1F2B' }}>
            <div style={{ fontSize: Tds.type.md, fontWeight: 800, letterSpacing: '0.04em' }}>Updating {APP_NAME}…</div>
            <div style={{ fontSize: Tds.type.xs, color: '#9298AC', marginTop: 6 }}>A new version was deployed. Reloading the latest.</div>
          </div>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', background: '#FFFFFF', color: '#e94560', minHeight: '100vh' }}>
          <h2 style={{ color: '#3A4258' }}>Something went wrong</h2>
          <pre style={{ background: '#FEF2F2', padding: 20, borderRadius: 8, overflow: 'auto', fontSize: Tds.type.sm, color: '#DC2626', border: '1px solid #FECACA' }}>
            {this.state.error?.message}{'\n'}{this.state.error?.stack?.slice(0, 500)}
          </pre>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ marginTop: 20, padding: '10px 24px', background: '#e94560', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: Tds.type.base }}>
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Lazy-loaded pages — each becomes its own chunk, loaded on demand ─────────
const Dashboard = lazyRetry(() => import('./pages/Dashboard'));
const MyWork = lazyRetry(() => import('./pages/MyWork'));
const Launchpad = lazyRetry(() => import('./pages/Launchpad'));
const ApprovalsInbox = lazyRetry(() => import('./pages/ApprovalsInbox'));
// InfluencerHub + Affiliate render inside the creator hub now.
const SkuMasterV2 = lazyRetry(() => import('./pages/SkuMasterV2'));
const Clients = lazyRetry(() => import('./pages/Clients'));
const RawMaterials = lazyRetry(() => import('./pages/RawMaterials'));
const RMInwards = lazyRetry(() => import('./pages/RMInwards'));
const ReadyGoodsInwards = lazyRetry(() => import('./pages/ReadyGoodsInwards'));
const Warehouse = lazyRetry(() => import('./pages/Warehouse'));
const Tasks = lazyRetry(() => import('./pages/Tasks'));

const Vendors = lazyRetry(() => import('./pages/Vendors'));
const SalesOrders = lazyRetry(() => import('./pages/SalesOrders'));
const UsersRoles = lazyRetry(() => import('./pages/UsersRoles'));
const ModuleAccessManager = lazyRetry(() => import('./pages/ModuleAccessManager'));
const HR = lazyRetry(() => import('./pages/HR'));
const QCDashboard  = lazyRetry(() => import('./pages/QCDashboard'));
const FinishedGoodsPage = lazyRetry(() => import('./pages/FinishedGoods'));
const InventoryScan = lazyRetry(() => import('./pages/InventoryScan'));
// AmazonIntelligence removed — using dedicated AMZ tool
const BatchCodes = lazyRetry(() => import('./pages/BatchCodes'));
const Attendance = lazyRetry(() => import('./pages/Attendance'));
const DispatchOps = lazyRetry(() => import('./pages/DispatchOps'));
const Calendar = lazyRetry(() => import('./pages/Calendar'));
const Quotations = lazyRetry(() => import('./pages/Quotations'));
const CourierPage = lazyRetry(() => import('./pages/Courier'));
const SalesInvoice = lazyRetry(() => import('./pages/SalesInvoice'));
const ApprovalRulesBuilder = lazyRetry(() => import('./pages/ApprovalRulesBuilder'));
const OrderToCashTimeline = lazyRetry(() => import('./pages/OrderToCashTimeline'));
const DebitNotes = lazyRetry(() => import('./pages/DebitNotes'));
const StockTransfer = lazyRetry(() => import('./pages/StockTransfer'));

const SecurityCenter = lazyRetry(() => import('./pages/SecurityCenter'));
const TwoFactorSetup = lazyRetry(() => import('./pages/TwoFactorSetup'));
const Passkeys = lazyRetry(() => import('./pages/Passkeys'));

const Salary = lazyRetry(() => import('./pages/Salary'));
const FaceEnrollment = lazyRetry(() => import('./pages/FaceEnrollment'));
const FaceSetup = lazyRetry(() => import('./pages/FaceSetup'));
const LeaveManagement = lazyRetry(() => import('./pages/LeaveManagement'));
const EmployeeDocuments = lazyRetry(() => import('./pages/EmployeeDocuments'));
const Payroll = lazyRetry(() => import('./pages/Payroll'));
const Returns = lazyRetry(() => import('./pages/Returns'));
const AnnouncementsAdmin = lazyRetry(() => import('./pages/AnnouncementsAdmin'));

// ── Buyer Portal pages ────────────────────────────────────────────────────────

// ── Partner Portal (exporters & resellers) ────────────────────────────────────

// Generic modules — lazy loaded via wrapper
// Same stale-deploy recovery as lazyRetry — these seven were the only lazy
// imports without it, so after a deploy they were the modules that "threw".
const lazyGeneric = (name) => React.lazy(() =>
  import('./pages/GenericModule').then(mod => ({ default: mod[name] })).catch((error) => {
    if (recoverFromStaleChunk()) return new Promise(() => {});
    throw error;
  })
);

// Vite preloads a route's chunks before the import resolves; when a deploy
// has renamed them, the failure surfaces here, not in the import's catch.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    if (recoverFromStaleChunk()) event.preventDefault();
  });
}

const ComplaintsModule = lazyGeneric('ComplaintsModule');
const QualityCompliance = lazyRetry(() => import('./pages/QualityCompliance'));

// Salesforce*.js were absorbed into /sales tabs (see RedirectSalesforce) and
// deleted 2026-09-10 after a month unrouted.
const InvoicesPage = lazyRetry(() => import('./pages/InvoicesPage'));
const PaymentsPage = lazyRetry(() => import('./pages/PaymentsPage'));
const ClientLedgerPage = lazyRetry(() => import('./pages/ClientLedgerPage'));
const GSTReturnsPage = lazyRetry(() => import('./pages/GSTReturnsPage'));
const ARAgingPage = lazyRetry(() => import('./pages/ARAgingPage'));
const CreditNotesPage = lazyRetry(() => import('./pages/CreditNotesPage'));
const QMSIncomingModule = lazyRetry(() => import('./pages/QMSIncoming'));
const QMSInProcessModule = lazyRetry(() => import('./pages/QMSInProcess'));
const QMSReleaseModule = lazyRetry(() => import('./pages/QMSRelease'));
const SettingsModule = lazyGeneric('SettingsModule');

// ── Investor Readiness ─────────────────────────────────────────────────────

// ── Module-native upgrade and control-layer pages ──────────────────────────

// ── Page loading — skeleton shimmer instead of spinner (feels faster) ────────
function PageLoader() {
  return (
    <div data-testid="page-loader" style={{ padding: '24px 28px', minHeight: '60vh' }}>
      <style>{`@keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`}</style>
      {/* Title bar skeleton */}
      <div style={{ height: 28, width: 200, borderRadius: 8, marginBottom: 20,
        background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)',
        backgroundSize: '800px 100%', animation: 'shimmer 1.2s infinite linear' }} />
      {/* KPI cards skeleton */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ flex: 1, height: 80, borderRadius: 12,
            background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)',
            backgroundSize: '800px 100%', animation: `shimmer 1.2s infinite linear ${i * 0.15}s` }} />
        ))}
      </div>
      {/* Table rows skeleton */}
      {[1,2,3,4,5].map(i => (
        <div key={i} style={{ height: 44, borderRadius: 8, marginBottom: 8,
          background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)',
          backgroundSize: '800px 100%', animation: `shimmer 1.2s infinite linear ${i * 0.1}s` }} />
      ))}
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { checkedIn } = useCheckInGate();
  if (loading || checkedIn === null) return <BootSplash />;
  if (!user) return <Navigate to="/login" />;
  if (!checkedIn) return <CheckInWall />;
  return children;
}

// Routes that all authenticated employees can access regardless of modules
const UNIVERSAL_ROUTES = new Set(['/', ...UNIVERSAL_MODULES]);

function ModuleGuard({ path, children }) {
  const { canAccess, user } = useAuth();
  // Founders bypass all module checks — they have access to everything by role.
  // This is intentional and also guards against stale JWTs that predate modules:[*].
  if (UNIVERSAL_ROUTES.has(path) || user?.role === 'founder' || canAccess(path)) return children;
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Tablet-facing face-recognition attendance kiosk — no login, gated by x-kiosk-token */}
          <Route path="/kiosk/attendance" element={<KioskAttendance />} />
          {/* /privacy is served by Express from backend/src/assets/privacy.html,
              above the SPA catch-all — no React route, so a reviewer can read it
              without the bundle loading. */}
          {/* Self-serve campaign forms — one page for every hub-created campaign */}
          {/* Content brief + self-serve go-live date picker — personal magic link */}
          {/* Vendor dispatch-status form — personal magic link, no login */}
          {/* Job card client approval — personal magic link, no login, no
              ModuleGuard: a client has no module grant on /jobs, so guarding
              this route would lock every client out of their own proof. */}
          {/* Filling Display — wall-mounted kiosk board, no login by design. */}

          {/* Public BMR QR landing — batch verification only, no login. */}

          {/* Staff bridge to the full BMR — self-gates, routing through login
              (with ?next back here) before handing off to the protected PDF. */}
          {/* Direct link into AERIS, already signed in (same tab, no popup).
              Handles its own login redirect (?next=) and check-in gate.
              Not /go/aeris: the backend mounts /go/:code (ambassador referral
              links, routes/affiliateGo.js) above the SPA, so a full page load
              of /go/aeris would land on the storefront. */}

          {/* ── Partner Portal — exporters & resellers ── */}
          {/* Public front door for exporters and resellers — no login. */}

          {/* ── B2B Buyer Portal — completely separate shell from the internal app ── */}

          <Route path="/*" element={
            <Protected>
              <Layout>
                <TallyKeyboardProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/my-work" element={<ModuleGuard path="/my-work"><MyWork /></ModuleGuard>} />
                    {/* Genuine renames only. A page that merely shares a grant
                        keeps its own route — redirecting it away from itself is
                        what sent "fragrance notes" to Formulations. */}
                    {Object.entries(LEGACY_PATH_REDIRECTS).map(([legacyPath, canonicalPath]) => (
                      <Route key={legacyPath} path={legacyPath} element={<Navigate to={canonicalPath} replace />} />
                    ))}
                    {/* Upload Hub is deliberately unguarded (like /tasks): every user may
                        SEE the directory; running an op is enforced per-operation server-side. */}

                    <Route path="/security" element={<ModuleGuard path="/security"><SecurityCenter /></ModuleGuard>} />
                    {/* No ModuleGuard: 2FA is an account-level control, so every
                        employee must be able to enrol regardless of module grants. */}
                    <Route path="/two-factor" element={<TwoFactorSetup />} />
                    <Route path="/passkeys" element={<Passkeys />} />
                    <Route path="/launchpad" element={<Launchpad />} />
                    <Route path="/approvals" element={<ApprovalsInbox />} />
                    {/* Growth Command Centre — creator programme V2. Same people as the
                        Squad Hub; the brand switcher inside scopes every tab. */}
                    <Route path="/skus" element={<ModuleGuard path="/skus"><SkuMasterV2 /></ModuleGuard>} />
                    <Route path="/clients" element={<ModuleGuard path="/clients"><Clients /></ModuleGuard>} />
                    <Route path="/rawmaterials" element={<ModuleGuard path="/rawmaterials"><RawMaterials /></ModuleGuard>} />
                    <Route path="/rm-inwards" element={<ModuleGuard path="/rm-inwards"><RMInwards /></ModuleGuard>} />
                    <Route path="/ready-goods-inwards" element={<ModuleGuard path="/ready-goods-inwards"><ReadyGoodsInwards /></ModuleGuard>} />
                    {/* Bench weigh mode lives under the batch-record path on purpose:
                        Layout's padlock checks canAccess(pathname) on the literal URL
                        and only prefix-matches a granted module, so /batch-sheets/…
                        would deny everyone who has /batch-record. That spelling is
                        kept as a redirect so old links still land. */}
                    <Route path="/warehouse" element={<ModuleGuard path="/warehouse"><Warehouse /></ModuleGuard>} />
                    <Route path="/tasks" element={<Tasks />} />
                    <Route path="/vendors" element={<ModuleGuard path="/vendors"><Vendors /></ModuleGuard>} />
                    <Route path="/sales-orders" element={<ModuleGuard path="/sales-orders"><SalesOrders /></ModuleGuard>} />
                    <Route path="/debit-notes" element={<ModuleGuard path="/debit-notes"><DebitNotes /></ModuleGuard>} />
                    <Route path="/payroll" element={<ModuleGuard path="/payroll"><Payroll /></ModuleGuard>} />
                    {/* Object page. Declared after the list so "/samples" keeps its own route. */}
                    <Route path="/users" element={<ModuleGuard path="/users"><UsersRoles /></ModuleGuard>} />
                    <Route path="/module-access" element={<ModuleGuard path="/module-access"><ModuleAccessManager /></ModuleGuard>} />
                    {/* R&D / PLM */}
                    {/* Operations */}
                    <Route path="/finished-goods" element={<ModuleGuard path="/finished-goods"><FinishedGoodsPage key="finished-goods" /></ModuleGuard>} />
                    <Route path="/inventory-scan" element={<ModuleGuard path="/inventory-scan"><InventoryScan key="inventory-scan" /></ModuleGuard>} />
                    <Route path="/stock-transfer" element={<ModuleGuard path="/stock-transfer"><StockTransfer key="stock-transfer" /></ModuleGuard>} />
                    <Route path="/batch-codes" element={<ModuleGuard path="/batch-codes"><BatchCodes key="batch-codes" /></ModuleGuard>} />
                    {/* Quality */}
                    <Route path="/qc" element={<ModuleGuard path="/qc"><QCDashboard key="qc" /></ModuleGuard>} />
                    <Route path="/qc-gate" element={<ModuleGuard path="/qc"><QCDashboard key="qc-gate" /></ModuleGuard>} />
                    <Route path="/qms/incoming" element={<ModuleGuard path="/qms/incoming"><QMSIncomingModule key="qms-incoming" /></ModuleGuard>} />
                    <Route path="/qms/inprocess" element={<ModuleGuard path="/qms/inprocess"><QMSInProcessModule key="qms-inprocess" /></ModuleGuard>} />
                    <Route path="/qms/release" element={<ModuleGuard path="/qms/release"><QMSReleaseModule key="qms-release" /></ModuleGuard>} />
                    <Route path="/capa" element={<ModuleGuard path="/capa"><QualityCompliance /></ModuleGuard>} />
                    <Route path="/complaints" element={<ModuleGuard path="/complaints"><ComplaintsModule key="complaints" /></ModuleGuard>} />
                    {/* Commerce */}
                    <Route path="/sales-invoice" element={<ModuleGuard path="/sales-invoice"><SalesInvoice key="sales-invoice" /></ModuleGuard>} />
                    <Route path="/approval-rules" element={<ModuleGuard path="/approval-rules"><ApprovalRulesBuilder /></ModuleGuard>} />
                    <Route path="/order-to-cash" element={<ModuleGuard path="/order-to-cash"><OrderToCashTimeline /></ModuleGuard>} />
                    <Route path="/quotations" element={<ModuleGuard path="/quotations"><Quotations key="quotations" /></ModuleGuard>} />
                    {/* Open to every authenticated user (no ModuleGuard) — same as Tasks/Calendar — so
                        "everyone knows what to use" is actually true. Only edit actions are module-gated. */}
                    {/* Folded into /social on 2026-09-12 for having no rows; restored 2026-09-23 with the
                        morning routine (approval-gated drafts) — the page is where drafts are approved. */}
                    {/* 2026-09-12 merges: twelve modules that never received a row now land where their job is done. */}
                    {/* Dispatch was a second front door to the same orders; the flow lives in Sales Orders (Mark Dispatched → courier → GRN). */}
                    <Route path="/dispatch" element={<ModuleGuard path="/dispatch"><Navigate to="/sales-orders?status=in_production" replace /></ModuleGuard>} />
                    <Route path="/dispatch-ops" element={<ModuleGuard path="/dispatch-ops"><DispatchOps key="dispatch-ops" /></ModuleGuard>} />
                    <Route path="/courier" element={<ModuleGuard path="/courier"><CourierPage key="courier" /></ModuleGuard>} />
                    {/* Sales module redesign — /sales is the command centre (leads →
                        deals → quotes → orders). The previous targets dashboard keeps
                        working at /sales/dashboard under the same grant. */}
                    {/* Reseller Portal */}
                    {/* Finance & People */}
                    <Route path="/invoices" element={<ModuleGuard path="/invoices"><InvoicesPage key="invoices" /></ModuleGuard>} />
                    <Route path="/payments" element={<ModuleGuard path="/payments"><PaymentsPage key="payments" /></ModuleGuard>} />
                    <Route path="/client-ledger" element={<ModuleGuard path="/client-ledger"><ClientLedgerPage key="client-ledger" /></ModuleGuard>} />
                    <Route path="/gst-returns" element={<ModuleGuard path="/gst-returns"><GSTReturnsPage key="gst-returns" /></ModuleGuard>} />
                    <Route path="/ar-aging" element={<ModuleGuard path="/ar-aging"><ARAgingPage key="ar-aging" /></ModuleGuard>} />
                    <Route path="/credit-notes" element={<ModuleGuard path="/credit-notes"><CreditNotesPage key="credit-notes" /></ModuleGuard>} />
                    <Route path="/hr" element={<ModuleGuard path="/hr"><HR key="hr" /></ModuleGuard>} />
                    <Route path="/salary" element={<ModuleGuard path="/salary"><Salary key="salary" /></ModuleGuard>} />
                    <Route path="/pulse-documents" element={<EmployeeDocuments key="pulse-documents" />} />
                    <Route path="/leave" element={<ModuleGuard path="/leave"><LeaveManagement key="leave" /></ModuleGuard>} />
                    <Route path="/attendance" element={<Attendance key="attendance" />} />
                    <Route path="/attendance/face-enrollment" element={<ModuleGuard path="/attendance/face-enrollment"><FaceEnrollment key="face-enrollment" /></ModuleGuard>} />
                    <Route path="/attendance/face-setup" element={<FaceSetup key="face-setup" />} />
                    {/* Creative */}
                    {/* Archive */}
                    {/* Security */}
                    {/* System */}
                    <Route path="/settings" element={<ModuleGuard path="/settings"><SettingsModule key="settings" /></ModuleGuard>} />
                    <Route path="/settings/announcements" element={<ModuleGuard path="/settings/announcements"><AnnouncementsAdmin /></ModuleGuard>} />
                    {/* B2B Buyer Control Tower — internal staff only */}
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </Suspense>
                <ButtonBar />
                </TallyKeyboardProvider>
              </Layout>
            </Protected>
          } />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
